// lineItemNotes.js — the "Line-item notes" checklist on the Xactimate report.
//
// Every line item should carry an F9 note; operators forget. This lists the ones
// that have none, grouped by room, so the estimator can find them in Xactimate and
// fix them before submitting.
//
// Presentation only. Computed at render time from parsed data. It never reads or
// writes findings, tiers, the hero or any dollar figure, and it is not a finding.
// verify-line-item-notes.js asserts that.
//
// Gates (render nothing rather than accuse the estimator because of our own read):
//   • not an Xactimate parse, or no line items
//   • parse predates notesVersion 2. Until 2026-09-24 the classic layout folded prose
//     F9 notes into the description, so an older parse under-counts notes. Stored
//     audits keep their parse until re-uploaded; a backfill re-runs rules, not the
//     parser. (docs/decisions/xactimate-f9-notes-classic-layout-2026-09-24.md)
//   • any item without a `notes` array (field absent ≠ no notes)
//   • document under-read (readQuality.underRead)

export const NOTES_MIN_VERSION = 2;
// At or above this many line items, a document with NO real notes gets the soft
// "we didn't find F9 notes" message instead of a list of every line.
export const SOFT_MESSAGE_MIN_ITEMS = 10;

// Xactimate's automatic line, not an estimator's note.
const WASTE_LINE = /^\d+(?:\.\d+)?\s*%\s*waste\s+added\b/i;
// A calc-residue word: operators between alphanumerics ("SF+WS19.SF+WS20.",
// "PF*3") or a bare Xactimate variable/measurement token ("WS19", "F12.5").
const CALC_WORD = /[A-Za-z0-9)][+*/=][A-Za-z0-9(]|^[A-Z]{1,4}\d+(?:\.\d+)?\.?$/;

/**
 * Is this one string an F9 note a person wrote? True only if, after dropping
 * Xactimate's waste line and calc-residue words, at least three letters of prose
 * remain in a row. "(2)air scrubbers x (5)days" → true; "SF+WS19.SF+WS20." → false.
 */
export function isRealNoteText(text) {
    const s = String(text || '').trim();
    if (!s || WASTE_LINE.test(s)) return false;
    const prose = s.split(/\s+/).filter(w => !CALC_WORD.test(w)).join(' ');
    return /[a-z]{3,}/i.test(prose);
}

/** Does this line item carry at least one real F9 note? (itemText never counts.) */
export function hasRealNote(item) {
    return Array.isArray(item && item.notes) && item.notes.some(isRealNoteText);
}

function lineCode(item) {
    if (item.cat || item.sel) return [item.cat, item.sel].filter(Boolean).join(' ');
    return item.code || '';
}

/**
 * @returns {null | { state: 'all-noted'|'none-found'|'missing', total, missingCount, groups }}
 *   null → render nothing. groups: [{ key, room, area, rows: [{ lineNumber, code, description }] }]
 */
export function buildNotesChecklist(parsedData) {
    const d = parsedData;
    if (!d || d.documentType !== 'Xactimate') return null;
    if (!(Number(d.notesVersion) >= NOTES_MIN_VERSION)) return null;
    if (d.readQuality && d.readQuality.underRead === true) return null;
    const items = Array.isArray(d.allLineItems) ? d.allLineItems : [];
    if (!items.length) return null;
    if (items.some(i => !Array.isArray(i && i.notes))) return null;

    const total = items.length;
    const missing = items.filter(i => !hasRealNote(i));
    if (missing.length === 0) return { state: 'all-noted', total, missingCount: 0, groups: [] };
    if (missing.length === total && total >= SOFT_MESSAGE_MIN_ITEMS) {
        return { state: 'none-found', total, missingCount: total, groups: [] };
    }

    const byKey = new Map();
    for (const i of missing) {
        const key = `${i.area || ''}|${i.room || ''}`;
        if (!byKey.has(key)) byKey.set(key, { key, room: i.room || null, area: i.area || null, rows: [] });
        byKey.get(key).rows.push({ lineNumber: i.lineNumber, code: lineCode(i), description: i.description || '' });
    }
    const groups = [...byKey.values()];
    for (const g of groups) g.rows.sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0));
    return { state: 'missing', total, missingCount: missing.length, groups };
}

/** Group heading: the room, qualified by its area only when two groups share a room name. */
export function groupLabel(group, groups) {
    const room = group.room || group.area || 'No room';
    const clash = group.room && groups.some(g => g !== group && g.room === group.room);
    return clash && group.area ? `${room} · ${group.area}` : room;
}
